import React from 'react';
import GameIcon from './GameIcon';

export default function Cart({ cart, coins, budgetLimit, onRemove, onCheckout, onClose, gameSettings = {} }) {
  const total = cart.reduce((sum, item) => sum + (item.cartPrice || item.base_price_tokens || 0), 0);
  const canAfford = coins >= total;
  const overBudget = budgetLimit > 0 && total > budgetLimit;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col animate-modal-content">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{gameSettings.cart_title || 'Your Cart'}</h3>
            <p className="text-xs text-gray-500">{gameSettings.cart_items_label ? `${cart.length} ${gameSettings.cart_items_label}` : `${cart.length} item${cart.length !== 1 ? 's' : ''}`}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5">
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl block mb-3"><GameIcon icon={gameSettings.icon_cart_menu || '🛒'} /></span>
              <p className="text-gray-500 text-sm">{gameSettings.cart_empty_title || 'Your cart is empty'}</p>
              <p className="text-gray-400 text-xs mt-1">{gameSettings.cart_empty_desc || 'Visit merchants and add items'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  {/* Item image */}
                  <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center text-xl overflow-hidden flex-shrink-0 border border-gray-100">
                    {item.image_url && (item.image_url.startsWith('http') || item.image_url.startsWith('/')) ? (
                      <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span><GameIcon icon={item.image_url && !item.image_url.startsWith('/') ? item.image_url : (item.icon || gameSettings.icon_default_item || '👕')} /></span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {item.name_english || item.name}
                    </p>
                    {item.name_darija && (
                      <p className="text-[10px] text-[#2D6A4F] truncate">{item.name_darija}</p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sm font-bold text-amber-700">
                        <GameIcon icon={gameSettings.icon_token || '🪙'} /> {item.cartPrice || item.base_price_tokens}
                      </span>
                      {item.cartPrice && item.cartPrice < item.base_price_tokens && (
                        <span className="text-[10px] text-green-600 font-medium">
                          Saved {item.base_price_tokens - item.cartPrice}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => onRemove(item.id)}
                    className="text-red-400 hover:text-red-600 text-sm p-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="p-5 border-t border-gray-100 flex-shrink-0 space-y-3">
            {/* Budget bar */}
            {budgetLimit > 0 && (
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-500">{gameSettings.cart_budget_label || 'Budget'}</span>
                  <span className={`font-semibold ${overBudget ? 'text-red-600' : 'text-gray-700'}`}>
                    {total} / {budgetLimit}
                  </span>
                </div>
                <div className="budget-bar-track">
                  <div
                    className="budget-bar-fill"
                    style={{
                      width: `${Math.min(100, (total / budgetLimit) * 100)}%`,
                      backgroundColor: overBudget ? '#DC2626' : total / budgetLimit > 0.8 ? '#D4A843' : '#16A34A'
                    }}
                  />
                </div>
                {overBudget && (
                  <p className="text-[10px] text-red-600 mt-1 font-medium">{gameSettings.cart_over_budget || "Over budget! You'll lose a star."}</p>
                )}
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="font-bold text-gray-900">{gameSettings.cart_total_label || 'Total'}</span>
              <span className="text-xl font-bold text-amber-700"><GameIcon icon={gameSettings.icon_token || '🪙'} /> {total}</span>
            </div>

            {/* Balance */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{gameSettings.cart_balance_label || 'Your balance'}</span>
              <span className={canAfford ? 'text-green-600' : 'text-red-600'}><GameIcon icon={gameSettings.icon_token || '🪙'} /> {coins}</span>
            </div>

            {!canAfford && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-700">
                {gameSettings.cart_not_enough_tokens ? gameSettings.cart_not_enough_tokens.replace('{amount}', total - coins) : `Not enough tokens! You need ${total - coins} more.`}
              </div>
            )}

            {/* Checkout */}
            <button
              onClick={onCheckout}
              disabled={!canAfford || cart.length === 0}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-colors ${
                canAfford
                  ? 'bg-[#2D6A4F] hover:bg-[#40916C] text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {canAfford ? (gameSettings.cart_checkout_button ? gameSettings.cart_checkout_button.replace('{total}', total) : `Checkout — 🪙 ${total}`) : (gameSettings.cart_not_enough_button || 'Not enough tokens')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
